import { useConvex } from "convex/react";
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useEditorStore } from "@/store/editorStore";
import {
  removeHotspot as removeLocalHotspot,
  updateHotspot as updateLocalHotspot,
} from "@/data/adapters/local/hotspots";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import type { Tables } from "@/types/database";
import { api } from "../../convex/_generated/api";

type Hotspot = Tables<"hotspots">;
type Panorama = Tables<"panoramas">;

export const useEditorActions = (
  uploadPanoramasImpl?: (
    files: FileList,
    uploadPreference?: "original-only" | "compressed-only",
    mediaType?: "panorama" | "zoomable_image",
  ) => Promise<void>,
  updateHotspotImpl?: (hotspotId: string, updates: Partial<Hotspot>) => Promise<void>,
  requestHotspotPlacementImpl?: (position: { x: number; y: number; z: number }) => Promise<void>,
  deleteHotspotImpl?: (hotspotId: string) => Promise<void>,
  updatePanoramaImpl?: (panoramaId: string, updates: Partial<Panorama>) => Promise<void>,
  deletePanoramaImpl?: (panoramaId: string) => Promise<void>,
) => {
  const convex = useConvex();
  const { toast } = useToast();
  const {
    setHotspots,
    setSelectedHotspot,
    pushDeletedHotspot,
    updateHotspot: updateHotspotInStore,
  } = useEditorStore();

  const toggleHotspotVisibility = useCallback(
    async (hotspotId: string) => {
      try {
        const currentHotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
        if (!currentHotspot) {
          throw new Error("Hotspot not found");
        }

        const snapshotBefore = {
          id: currentHotspot.id,
          project_id: currentHotspot.project_id,
          panorama_id: currentHotspot.panorama_id,
          name: currentHotspot.name,
          type: currentHotspot.type,
          content: currentHotspot.content,
          position: currentHotspot.position,
          rotation: currentHotspot.rotation,
          scale: currentHotspot.scale,
          visible: currentHotspot.visible,
          order: currentHotspot.order,
          cms_action: (currentHotspot as { cms_action?: unknown }).cms_action,
          cmsAction: (currentHotspot as { cmsAction?: unknown }).cmsAction,
        };

        // Toggle the visibility
        const newVisible = !currentHotspot.visible;

        const useLocalBackend =
          isElectron() && useEditorStore.getState().projectBackend === "local";

        // Update with toggled visibility
        if (useLocalBackend) {
          const craftspaceId = useEditorStore.getState().project?.craftspace_id || "default";
          await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, visible: newVisible });
        } else {
          await convex.mutation(api.hotspots.update, { id: hotspotId, visible: newVisible });
        }

        // Update store
        updateHotspotInStore(hotspotId, { visible: newVisible });
        const snapshotAfter = { ...snapshotBefore, visible: newVisible };
        useEditorStore.getState().recordHotspotUpdateWithSnapshots(
          hotspotId,
          snapshotBefore,
          snapshotAfter,
        );

        // Debug: Log the update
        console.log("🔍 Hotspot visibility updated:", {
          hotspotId,
          newVisible,
          storeHotspots: useEditorStore.getState().hotspots.find((h) => h.id === hotspotId),
        });
      } catch (error) {
        console.error("Error updating hotspot visibility:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to update hotspot visibility",
        });
      }
    },
    [convex, updateHotspotInStore, toast],
  );

  const deleteHotspotWithUndo = useCallback(
    async (hotspotId: string) => {
      try {
        // Get the hotspot before deleting
        const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
        if (!hotspot) {
          throw new Error("Hotspot not found");
        }

        const useLocalBackend = isElectron() && useEditorStore.getState().projectBackend === "local";

        // Delete from database
        if (useLocalBackend) {
          const craftspaceId = useEditorStore.getState().project?.craftspace_id || "default";
          await removeLocalHotspot(hotspotId, craftspaceId);
        } else {
          await convex.mutation(api.hotspots.remove, { id: hotspotId });
        }

        // Update store
        const currentHotspots = useEditorStore.getState().hotspots;
        setHotspots(currentHotspots.filter((h) => h.id !== hotspotId));
        const currentSelectedHotspot = useEditorStore.getState().selectedHotspot;
        setSelectedHotspot(
          currentSelectedHotspot?.id === hotspotId ? null : currentSelectedHotspot,
        );
        pushDeletedHotspot(hotspot);
        useEditorStore.getState().recordHotspotDelete(hotspot);

      } catch (error) {
        console.error("Error deleting hotspot:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete hotspot",
        });
      }
    },
    [convex, setHotspots, setSelectedHotspot, pushDeletedHotspot, toast],
  );

  const uploadPanoramas = useCallback(
    async (
      files: FileList,
      uploadPreference?: "original-only" | "compressed-only",
      mediaType?: "panorama" | "zoomable_image",
    ) => {
      if (uploadPanoramasImpl) {
        await uploadPanoramasImpl(files, uploadPreference, mediaType);
      } else {
        console.warn("uploadPanoramas not implemented - should be provided by Editor component");
      }
    },
    [uploadPanoramasImpl],
  );

  const updateHotspotFromViewer = useCallback(
    async (hotspotId: string, updates: Partial<Hotspot>) => {
      if (updateHotspotImpl) {
        await updateHotspotImpl(hotspotId, updates);
      } else {
        console.warn(
          "updateHotspotFromViewer not implemented - should be provided by Editor component",
        );
      }
    },
    [updateHotspotImpl],
  );

  const requestHotspotPlacement = useCallback(
    async (position: { x: number; y: number; z: number }) => {
      if (requestHotspotPlacementImpl) {
        await requestHotspotPlacementImpl(position);
      } else {
        console.warn(
          "requestHotspotPlacement not implemented - should be provided by Editor component",
        );
      }
    },
    [requestHotspotPlacementImpl],
  );

  const updateHotspotFromPanel = useCallback(
    async (hotspotId: string, updates: Partial<Hotspot>) => {
      if (updates.content && (updates.content as Record<string, unknown>).text !== undefined) {
        const textValue = (updates.content as Record<string, unknown>).text;
        console.log("[Hotspot] update from panel", {
          id: hotspotId,
          textLength: typeof textValue === "string" ? textValue.length : null,
        });
      }
      if (updateHotspotImpl) {
        await updateHotspotImpl(hotspotId, updates);
      } else {
        console.warn(
          "updateHotspotFromPanel not implemented - should be provided by Editor component",
        );
      }
    },
    [updateHotspotImpl],
  );

  const _deleteHotspotFromPanel = useCallback(
    async (hotspotId: string) => {
      if (deleteHotspotImpl) {
        await deleteHotspotImpl(hotspotId);
      } else {
        console.warn(
          "deleteHotspotFromPanel not implemented - should be provided by Editor component",
        );
      }
    },
    [deleteHotspotImpl],
  );

  const updatePanoramaFromPanel = useCallback(
    async (panoramaId: string, updates: Partial<Panorama>) => {
      if (updatePanoramaImpl) {
        await updatePanoramaImpl(panoramaId, updates);
      } else {
        console.warn(
          "updatePanoramaFromPanel not implemented - should be provided by Editor component",
        );
      }
    },
    [updatePanoramaImpl],
  );

  const deletePanoramaFromPanel = useCallback(
    async (panoramaId: string) => {
      if (deletePanoramaImpl) {
        await deletePanoramaImpl(panoramaId);
      } else {
        // Fallback: soft delete by removing DB record only
        console.warn(
          "deletePanoramaFromPanel not implemented - should be provided by Editor component",
        );
      }
    },
    [deletePanoramaImpl],
  );

  return {
    toggleHotspotVisibility,
    deleteHotspotWithUndo,
    uploadPanoramas,
    updateHotspotFromViewer,
    requestHotspotPlacement,
    updateHotspotFromPanel,
    deletePanoramaFromPanel,
    updatePanoramaFromPanel,
  };
};
